import { AccountBalance, CalendarMonthOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, Snackbar, TextField, Typography } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AccordionSection from '../../components/AccordionSection';
import ConfirmationModal from '../../components/ConfirmationModal';
import ActivityLogsSection from '../../components/activityLogs/ActivityLogsSection';
import AttachmentsSection from '../../components/attachments/AttachmentsSection';
import AccountSearchAutocomplete from '../../components/common/AccountSearchAutocomplete/AccountSearchAutocomplete';
import EmailTemplatesSection, { EmailTemplateSectionModule } from '../../components/common/EmailTemplatesSection/EmailTemplatesSection';
import FieldsSection from '../../components/common/FieldsSection/FieldsSection';
import ItemHeader from '../../components/common/ItemHeader/ItemHeader';
import { InfoItem } from '../../components/common/ItemHeader/ItemHeaderInfo';
import MoreOptions from '../../components/common/MoreOptions/MoreOptions';
import TagsSection from '../../components/common/TagsSection/TagsSection';
import TextFieldWithLink from '../../components/common/TextFieldWithLink/TextFieldWithLink';
import ContactTable from '../../components/events/ContactTable/ContactTable';
import EmployeeTable from '../../components/events/EmployeeTable/EmployeeTable';
import EventAddress from '../../components/events/EventAddress/EventAddress';
import IntegrationSection from '../../components/events/IntegrationsSection/IntegrationsSection';
import TasksSection from '../../components/events/TasksSection/TasksSection';
import NotesSection from '../../components/notes/NotesSection';
import PreferencePicker from '../../components/preferences/picker/PreferencePicker';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../routes/Routes';
import { useGetAccountById } from '../../services/react-query/accounts';
import { useCreateEvent, useDestroyEvent, useGetEventById, useUpdateEvent } from '../../services/react-query/events';
import { useArchiveEvent } from '../../services/react-query/events/useArchiveEvent';
import { useDuplicateEvent } from '../../services/react-query/events/useDuplicateEvent';
import { mutationKeys } from '../../services/react-query/mutationKeys';
import { useGetAllPreferences } from '../../services/react-query/preferences';
import {
  inputContainerStyling,
  inputDividerStyling,
  itemActivitySectionStyling,
  itemContainerStyling,
  itemInnerLayoutStyling,
  itemWrapperStyling,
} from '../../styles/sxStyling';
import { ActivityLogOwnerType } from '../../types/ActivityLog';
import { Address } from '../../types/Address';
import { AttachmentOwnerType } from '../../types/Attachment';
import { Event, EventStatus } from '../../types/Event';
import { NoteableType } from '../../types/Note';
import { PREFERENCES_VALID_KEYS, Preference } from '../../types/Preference';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../../types/User';
import { Loading } from '../../uikit';
import { humanizeEventDateTimes, parseIsoWithDefault, singleDateEvent } from '../../utility/dateUtils';
import { noTzIsoOptions } from '../../utility/event';
import { errorPopulator, uuidFinder } from '../../utility/inputUtils';
import SubeventsSection from './SubeventsSection';
import EventDuplicateModal from './EventDuplicationModal';
import EmployeeRequirementSection from '../../components/common/EmployeeRequirementSection/EmployeeRequirementSection';

const EVENT_STATUS_OPTIONS: EventStatus[] = Object.keys(EventStatus).map((k) => EventStatus[k as keyof typeof EventStatus]);

const EventItem: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();

  const [event, setEvent] = useState<Event | null>(null);
  const [saved, setSaved] = useState(false);
  const [isFullDayEvent, setIsFullDayEvent] = useState(true);
  const [deletionConfirmationOpen, setDeletionConfirmationOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const defaultDuplicateName = event?.name ? `${event.name} Copy` : 'Copy';

  const eventTemplateUuidFromParams = searchParams.get('template') || undefined;
  const accountUuidFromParams = searchParams.get('account') || undefined;
  const parentUuidFromParams = searchParams.get('parent') || undefined;

  const { data: eventData } = useGetEventById({
    uuid: _.toString(uuid),
    template: eventTemplateUuidFromParams,
    parent: parentUuidFromParams,
  });

  const accountUuidFromEvent = event?.account_uuid;
  const { data: existingAccountData } = useGetAccountById({
    uuid: _.toString(accountUuidFromParams || accountUuidFromEvent),
    enabled: !!accountUuidFromParams || !!accountUuidFromEvent,
  });

  const { data: preferencesData, isFetching: isGetAllPreferencesFetching } = useGetAllPreferences({});

  const { mutate: createEvent } = useCreateEvent({
    onSuccessCallback: (data) => navigate(`/${ROUTE_PATHS.EVENTS}/${data.uuid}`),
  });
  const { mutate: updateEvent } = useUpdateEvent({
    uuid: _.toString(uuid),
    onSuccessCallback: () => setSaved(true),
  });
  const { mutate: duplicateEvent } = useDuplicateEvent({
    uuid: _.toString(uuid),
    onSuccess: (newEvent: Event) => {
      setIsDuplicateModalOpen(false);
      navigate(`/${ROUTE_PATHS.EVENTS}/${newEvent.uuid}`);
    },
  });

  const isCreateEventMutation = useIsMutating({ mutationKey: [mutationKeys.createEvent] });
  const isUpdateEventMutation = useIsMutating({ mutationKey: [mutationKeys.updateEvent] });

  const returnToList = () => navigate(`/${ROUTE_PATHS.EVENTS_TABLE}`);
  const { mutate: destroyEvent } = useDestroyEvent({ uuid: _.toString(uuid), onSuccessCallback: returnToList });
  const { mutate: archiveEvent } = useArchiveEvent({ uuid: _.toString(uuid), onSuccessCallback: returnToList });

  useEffect(() => {
    if (!eventData) return;

    const singleDay = singleDateEvent(eventData);
    setIsFullDayEvent(singleDay);

    const startDt = DateTime.fromISO(eventData.start_at);
    const endDt = DateTime.fromISO(eventData.end_at);
    const start_at = (singleDay ? startDt.toISODate() : startDt.toISO(noTzIsoOptions)) || '';
    const end_at = endDt.toISO(noTzIsoOptions) || '';

    setEvent({
      ...eventData,
      start_at,
      end_at,
      account_uuid: eventData.account_uuid || accountUuidFromParams || '',
      event_template_uuid: eventData.event_template_uuid || eventTemplateUuidFromParams,
    });
  }, [eventData, accountUuidFromParams, eventTemplateUuidFromParams]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable('archived', !!event?.discarded_at);

  const preferences = preferencesData || [];
  const accounts = existingAccountData ? [existingAccountData] : [];
  const addresses = existingAccountData?.addresses || [];
  const isNew = uuid === 'new';

  if (!event || !eventData) return <Loading />;

  const updateEventData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setEvent({ ...event, [id]: value });
  };

  const handleAddressChange = (newAddress: Address | undefined) => {
    setEvent((prev) => (prev ? { ...prev, address: newAddress || undefined } : prev));
  };

  const handleSave = () => {
    const payload = {
      ...event,
      is_full_day: isFullDayEvent,
      start_at: DateTime.fromISO(event.start_at).toISO() || '',
      end_at: DateTime.fromISO(event.end_at).toISO() || '',
    };
    if (isNew) createEvent(payload);
    else updateEvent(payload);
  };

  const toggleIsFullDayEvent = () => {
    setIsFullDayEvent((v) => !v);
    setEvent((prev) => (prev ? { ...prev, start_at: '', end_at: '' } : prev));
  };

  type PrefUuidKey = 'type_uuid' | 'location_uuid';
  const setPrefUuid = (key: PrefUuidKey) => (p: Preference | null | undefined) => {
    setEvent((prev) => (prev ? { ...prev, [key]: p?.uuid ?? undefined } : prev));
  };

  const infoItems = [
    eventData ? { icon: <CalendarMonthOutlined fontSize="small" color="action" />, content: humanizeEventDateTimes(eventData) } : null,
    eventData?.account_name ? { icon: <AccountBalance fontSize="small" color="action" />, content: eventData.account_name } : null,
  ].filter(Boolean) as InfoItem[];

  const updateDate = (value: DateTime | null, name: 'start_at' | 'end_at') => {
    if (!value || !value.isValid) return;

    const newEvent = { ...event, [name]: value.toISO() };

    setEvent({
      ...newEvent,
      children: _.map(event.children, (child) => {
        if (_.isNil(child.event_template_parent_child_offset)) return child;

        const eventStart = DateTime.fromISO(newEvent.start_at);
        if (!eventStart.isValid) return child;

        // true generic asserts these are valid dates
        const childStart: DateTime<true> = eventStart.plus({ days: child.event_template_parent_child_offset });
        return { ...child, start_at: childStart.toISO(), end_at: childStart.plus({ day: 1 }).toISO() };
      }),
    });
  };

  const accountBlock =
    event.multi_child && event.parent_uuid ? (
      <Box sx={{ display: 'inline-flex', gap: 2 }}>
        <TextFieldWithLink
          disabled
          linkType="internal"
          label="Account"
          to={`/${ROUTE_PATHS.ACCOUNTS}/${event.account_uuid}`}
          value={event.account_display_name}
        />
        <TextFieldWithLink
          disabled
          linkType="internal"
          label="Group Event"
          to={`/${ROUTE_PATHS.EVENTS}/${event.parent_uuid}`}
          value={event.parent_name ?? ''}
        />
      </Box>
    ) : (
      <AccountSearchAutocomplete
        value={uuidFinder(accounts, event.account_uuid) || uuidFinder(accounts, accountUuidFromParams)}
        accountsOverride={_.isEmpty(accountUuidFromParams) ? undefined : accounts}
        error={errorPopulator(event, 'account')}
        onChange={(account) => setEvent({ ...event, account_uuid: account?.uuid || '' })}
      />
    );

  const StartDateInput = isFullDayEvent ? DatePicker : DateTimePicker;

  return (
    <Box sx={itemWrapperStyling}>
      <ItemHeader
        title={isNew ? 'Create Event' : eventData.name}
        onSave={handleSave}
        archived={!!event.discarded_at}
        disableActions={Boolean(isCreateEventMutation || isUpdateEventMutation)}
        showMoreOptions={
          !isNew ? (
            <MoreOptions
              onDelete={destroyEvent}
              onArchive={archiveEvent}
              name={event.name}
              itemName="event"
              onDuplicate={() => setIsDuplicateModalOpen(true)}
            />
          ) : null
        }
        module={UserConfigurableModule.EVENTS}
        infoItems={infoItems}
      />

      {/* Left Side */}
      <Box sx={itemInnerLayoutStyling}>
        <Box sx={itemContainerStyling}>
          <AccordionSection title="Event Information" collapseId={UserSettingsCollapseKeys.EVENT_ITEM_INFORMATION}>
            <Box sx={inputContainerStyling}>
              <Box sx={inputDividerStyling}>
                <TextField id="name" label="Event Name" value={event.name} onChange={updateEventData} disabled={disabled} {...errorPopulator(event, 'name')} />
                {accountBlock}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StartDateInput
                    label="Start Date"
                    onChange={(value) => updateDate(value, 'start_at')}
                    value={parseIsoWithDefault(event.start_at)}
                    {...errorPopulator(event, 'start_at')}
                    disabled={disabled}
                  />
                  <Box sx={{ minWidth: '8rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>Full Day Event</Typography>
                    <Checkbox size="small" sx={{ p: 0 }} checked={isFullDayEvent} onChange={toggleIsFullDayEvent} disabled={disabled} />
                  </Box>
                </Box>
                {!isFullDayEvent && (
                  <DateTimePicker
                    label="End Date"
                    onChange={(value) => updateDate(value, 'end_at')}
                    value={parseIsoWithDefault(event.end_at)}
                    minDateTime={parseIsoWithDefault(event.start_at)}
                    {...errorPopulator(event, 'end_at')}
                    disabled={disabled}
                  />
                )}
              </Box>

              <Box sx={inputDividerStyling}>
                {/* Event Type */}
                <PreferencePicker
                  label="Event Type"
                  options={_.filter(preferences, { key: PREFERENCES_VALID_KEYS.EVENT_TYPE })}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(preferences, event?.type_uuid) as Preference | null}
                  onValueChange={setPrefUuid('type_uuid')}
                  disabled={disabled}
                  error={!!errorPopulator(event, 'type')?.error}
                  helperText={errorPopulator(event, 'type')?.helperText}
                />

                {/* Event Location */}
                <PreferencePicker
                  label="Event Location"
                  options={_.filter(preferences, { key: PREFERENCES_VALID_KEYS.EVENT_LOCATION })}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(preferences, event?.location_uuid) as Preference | null}
                  onValueChange={setPrefUuid('location_uuid')}
                  disabled={disabled}
                  error={!!errorPopulator(event, 'location')?.error}
                  helperText={errorPopulator(event, 'location')?.helperText}
                />

                {/* Event Status (native Autocomplete over enum) */}
                <Autocomplete<EventStatus>
                  options={EVENT_STATUS_OPTIONS}
                  onChange={(_, status) => setEvent({ ...event, status } as Event)}
                  value={event?.status}
                  disabled={disabled}
                  renderInput={(params) => <TextField {...params} {...errorPopulator(event, 'status')} label="Event Status" />}
                />
              </Box>

              <EventAddress address={event.address || null} accountAddresses={addresses} onAddressChange={handleAddressChange} />
            </Box>
          </AccordionSection>

          <FieldsSection
            fields={event.fields}
            setFields={(fields) => setEvent({ ...event, fields })}
            collapseId={UserSettingsCollapseKeys.EVENT_ITEM_CUSTOM_FIELDS}
          />

          {event.integrations.length > 0 && <IntegrationSection integrations={event.integrations} />}

          {!event.multi_child && <SubeventsSection events={event.children} setEvents={(children) => setEvent({ ...event, children })} />}

          <TasksSection event={event} setTasks={(tasks) => setEvent({ ...event, tasks })} />

          <EmailTemplatesSection
            moduleType={EmailTemplateSectionModule.EVENT}
            linkingModels={event.event_email_templates}
            setLinkingModels={(linkingModels) => setEvent({ ...event, event_email_templates: linkingModels })}
            collapseId={UserSettingsCollapseKeys.EVENT_ITEM_EMAIL_TEMPLATES}
            startDate={event.start_at}
            availableCustomFields={_.map(event.fields, 'label')}
            eventContacts={event.contacts}
            dependencyOptions={event.tasks}
          />

          <EmployeeRequirementSection
            collapseId={UserSettingsCollapseKeys.EVENT_ITEM_EMPLOYEE_REQUIREMENTS}
            employeeRequirements={event.employee_requirements || []}
            setEmployeeRequirements={(reqs) => setEvent({ ...event, employee_requirements: reqs })}
          />

          <EmployeeTable
            eventEmployees={event.event_employees}
            setEventEmployees={(eventEmployees) => setEvent({ ...event, event_employees: eventEmployees })}
          />

          <ContactTable accountUuid={event.account_uuid} contacts={event.contacts} setContacts={(contacts) => setEvent({ ...event, contacts })} />

          <TagsSection tags={event.tags} collapseId={UserSettingsCollapseKeys.EVENT_ITEM_TAGS} setTags={(tags) => setEvent({ ...event, tags })} />

          {!isNew && (
            <AttachmentsSection collapseId={UserSettingsCollapseKeys.EVENT_ITEM_ATTACHMENTS} ownerType={AttachmentOwnerType.EVENT} ownerUuid={uuid ?? ''} />
          )}

          <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
          <ConfirmationModal
            isOpen={deletionConfirmationOpen}
            onCancel={() => setDeletionConfirmationOpen(false)}
            onConfirm={destroyEvent}
            text={`Are you sure you want to delete the event ${event.display_name}? This operation is irreversible!`}
          />
        </Box>

        {/* Right Side */}
        {!isNew && (
          <Box sx={itemActivitySectionStyling}>
            <ActivityLogsSection ownerUuid={uuid} ownerType={ActivityLogOwnerType.EVENT} collapseId={UserSettingsCollapseKeys.EVENT_ITEM_ACTIVITY_LOG} />
            <NotesSection collapseId={UserSettingsCollapseKeys.EVENT_ITEM_NOTES} noteable={{ noteableUuid: uuid ?? '', noteableType: NoteableType.EVENT }} />
          </Box>
        )}
      </Box>

      <EventDuplicateModal
        isOpen={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onConfirm={(payload) => duplicateEvent(payload)}
        defaultName={defaultDuplicateName}
        itemName="event"
      />
    </Box>
  );
};

export default EventItem;
